// <emma-chat-card> — single result card.
//
// Layout 1:1 lifted from `design-system/components/provider-card.css` +
// `provider-card.html`. Horizontal `.provider-card-top` row contains
// [provider-block | location-block | price]; `.provider-card-tags` row
// below carries ALL spec pills (gpu, vcpu, ram, disk, OS, spot indicator).
// Best-price inline-text in price column (NOT a floating badge — DS spec).
//
// Two body variants:
//   - Default (VM/Spot): single price in `.provider-card-price`
//   - Kubernetes (when `nodeMin/Max` set): autoscale price RANGE in price column,
//     plus a `Pricing range` label above (widget extension — DS has no K8s atom)
//
// Click → `emma-card-click` (bubbles + composed) with detail
// `{cardId, ctaUrl, configId}`. Keyboard accessible (Enter / Space).
//
// Animation: `card-enter` keyframes lifted from DS — fade + 6px translateY,
// stagger via `--card-index` set inline by parent (T09 sets per-card).

import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { ResultCard, PriceUnit } from '../types/index.js';
import { assetUrl } from '../util/asset-url.js';
import { designTokens } from './design-tokens.js';
import { tagStyles } from './shared-styles.js';
import { k8sNodeCountLabel } from './card-format.js';

@customElement('emma-chat-card')
export class EmmaChatCard extends LitElement {
  /** Full ResultCard payload. Required. */
  @property({ attribute: false })
  card!: ResultCard;

  /** Display unit. Drives whether hour or month price is shown. */
  @property({ type: String, attribute: 'price-unit' })
  priceUnit: PriceUnit = 'hour';

  /** Synthetic ID like "t0c1" used for pick-from-results lookups. */
  @property({ type: String, attribute: 'card-id' })
  cardId = '';

  /** Highlight as selected. */
  @property({ type: Boolean, reflect: true })
  selected = false;

  /** Highlight as user-picked in pick_from_results mode. */
  @property({ type: Boolean, reflect: true })
  picked = false;

  /** BFF URL prefix for asset resolution (provider logo, country flag,
   *  spot icon). Empty = same-origin (landing). Set by parent <emma-chat>
   *  via attribute drilling through message-list → turn → card. */
  @property({ type: String, attribute: 'api-base' })
  apiBase = '';

  static override styles = [
    designTokens,
    tagStyles,
    // Provider-card styles lifted 1:1 from
    // bff/static/design-system/components/provider-card.css.
    // Inside the card's Shadow DOM these are private to this element.
    css`
      :host {
        display: block;
      }

      /* Provider logo — 24x24 white circle, 15x15 inner img (DS-card-context
         override of the standalone provider-logo.css 11x11 baseline) */
      .provider-logo {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 1px solid var(--gray-10);
        background: var(--white);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
      .provider-logo img {
        width: 15px;
        height: 15px;
      }

      /* Best price — inline-text in price column, NOT a floating badge */
      .provider-card-best {
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--green-60);
      }
      .provider-card-best.is-pulsing {
        animation: best-badge-arrive var(--duration-pulse) ease-in-out both;
      }
      @keyframes best-badge-arrive {
        0%, 100% { transform: scale(1); }
        50%       { transform: scale(1.2); }
      }

      /* Stagger entry — parent sets style="--card-index: N" per card */
      @keyframes card-enter {
        from {
          opacity: 0;
          transform: translateY(6px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Provider card root.
         Box-shadow uses INSET (was outer 0 0 0 N) so the gray/selected/
         picked ring renders strictly inside the card box. The outer
         variant extended 1-2px past the card edge and got clipped by
         the parent .results-peek-inner overflow:hidden (used by the
         grid-rows reveal trick). Inset keeps the ring within the box,
         no clipping, and still gives the same visual border effect. The
         card's own padding (12-16px) keeps content well clear of the
         2px-thick ring. */
      .provider-card {
        position: relative;
        container-type: inline-size;
        border: 1px solid transparent;
        box-shadow: inset 0 0 0 1px var(--gray-10);
        border-radius: var(--radius-md);
        padding: var(--space-3) var(--space-4);
        background: var(--white);
        cursor: pointer;
        transition: box-shadow var(--duration-fast) var(--easing);
        animation: card-enter var(--duration-normal) var(--easing-out) both;
        animation-delay: calc(var(--card-index, 0) * 60ms);
      }
      .provider-card:hover {
        box-shadow: inset 0 0 0 1px var(--gray-20);
      }
      :host([selected]) .provider-card,
      .provider-card.provider-card--selected {
        box-shadow: inset 0 0 0 2px var(--blue-60);
        background: var(--blue-03);
      }
      :host([picked]) .provider-card {
        box-shadow: inset 0 0 0 2px var(--green-60);
        background: var(--green-03);
      }
      :host([selected]) .provider-card-best,
      :host([picked]) .provider-card-best {
        animation: best-badge-arrive var(--duration-pulse) var(--easing-spring) both;
      }

      .provider-card-top {
        display: flex;
        align-items: center;
        gap: var(--space-4);
      }
      .provider-card-provider {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-shrink: 0;
      }
      .provider-card-name {
        font-size: var(--text-md);
        font-weight: var(--font-medium);
        color: var(--color-text);
        white-space: nowrap;
      }
      .provider-card-loc {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-size: var(--text-xs);
        color: var(--color-text-muted);
        flex: 1;
        min-width: 0;
        padding-left: var(--space-1);
      }
      .provider-card-loc-region {
        color: var(--color-text-subtle);
      }
      .provider-flag {
        width: 14px;
        height: 14px;
        border-radius: 2px;
        object-fit: cover;
        flex-shrink: 0;
      }
      .provider-card-price {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-mono);
        font-size: var(--text-md);
        font-weight: var(--font-semibold);
        color: var(--color-text);
        white-space: nowrap;
        margin-left: auto;
      }
      .provider-card-price-secondary {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        font-weight: var(--font-regular);
        color: var(--color-text-muted);
      }
      .provider-card-tags {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-1);
        margin-top: var(--space-2);
      }

      /* Tag icons (spot, k8s) — 12px, monolith pattern */
      .spot-icon,
      .k8s-icon {
        width: 12px;
        height: 12px;
        opacity: 0.7;
        flex-shrink: 0;
      }

      /* Container query (DS pattern — responds to OWN width, not viewport) */
      @container (max-width: 480px) {
        .provider-card-top {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0;
          row-gap: var(--space-1);
        }
        .provider-card-provider {
          flex: 1;
          order: 1;
        }
        .provider-card-price {
          order: 2;
        }
        .provider-card-loc {
          order: 3;
          flex: 0 0 100%;
          padding-left: var(--space-1);
        }
        /* Collapse "Best price" text to a green dot on narrow cards */
        .provider-card-best {
          font-size: 0;
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--green-60);
          flex-shrink: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        /* :host([selected]) / :host([picked]) selectors above are
           higher-specificity than the bare class — repeat them here so
           the override actually wins under reduced-motion. */
        .provider-card,
        :host([selected]) .provider-card,
        :host([picked]) .provider-card {
          animation: none;
          transition: none;
        }
        .provider-card-best,
        .provider-card-best.is-pulsing,
        :host([selected]) .provider-card-best,
        :host([picked]) .provider-card-best {
          animation: none;
        }
      }
    `,
  ];

  private handleClick = (): void => {
    this.dispatchEvent(
      new CustomEvent('emma-card-click', {
        detail: {
          cardId: this.cardId,
          ctaUrl: this.card?.ctaUrl ?? '',
          configId: this.card?.configId ?? '',
        },
        bubbles: true,
        composed: true,
      }),
    );
  };

  private handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleClick();
    }
  };

  override render() {
    const card = this.card;
    if (!card) return nothing;

    const isK8s =
      card.workloadType === 'kubernetes' &&
      card.nodeMin !== undefined &&
      card.nodeMax !== undefined;

    return html`
      <div
        class="provider-card${this.selected ? ' provider-card--selected' : ''}"
        role="button"
        tabindex="0"
        aria-label="${card.provider} ${card.locationDisplay}"
        @click=${this.handleClick}
        @keydown=${this.handleKeydown}
      >
        <div class="provider-card-top">
          <div class="provider-card-provider">
            ${card.providerLogo
              ? html`<div class="provider-logo">
                  <img
                    src=${assetUrl(this.apiBase, card.providerLogo)}
                    alt="${card.provider}"
                  />
                </div>`
              : nothing}
            <div class="provider-card-name">${card.provider}</div>
          </div>
          <div class="provider-card-loc">
            ${card.country
              ? html`<img
                  class="provider-flag"
                  src=${assetUrl(this.apiBase, `/assets/icons/country/${card.country}.svg`)}
                  alt=""
                />`
              : nothing}
            <span>${card.locationDisplay}</span>
            ${card.continent
              ? html`&nbsp;·&nbsp;<span class="provider-card-loc-region"
                    >${card.continent}</span
                  >`
              : nothing}
          </div>
          ${isK8s ? this.renderK8sPrice() : this.renderVmPrice()}
        </div>
        ${this.renderTags()}
      </div>
    `;
  }

  private renderVmPrice() {
    const card = this.card;
    const primary =
      this.priceUnit === 'month' ? card.pricePerMonth : card.pricePerHour;
    const secondary =
      this.priceUnit === 'month' ? card.pricePerHour : card.pricePerMonth;
    return html`
      <div class="provider-card-price">
        ${card.isBestPrice
          ? html`<span class="provider-card-best is-pulsing">Best price</span>`
          : nothing}
        <span>${primary}</span>
        ${secondary
          ? html`<span class="provider-card-price-secondary">(${secondary})</span>`
          : nothing}
      </div>
    `;
  }

  private renderK8sPrice() {
    const card = this.card;
    const minPrice =
      this.priceUnit === 'month' ? card.priceMinMonth : card.priceMinHour;
    const maxPrice =
      this.priceUnit === 'month' ? card.priceMaxMonth : card.priceMaxHour;
    // Worker mode (or any degenerate min == max) is a FIXED size, not a scaling
    // range — show one per-node price instead of "€X → €X" (same principle as
    // collapsing "1–1 nodes" → "1 node").
    const single = card.k8sMode === 'worker' || minPrice === maxPrice;
    return html`
      <div class="provider-card-price">
        <span>${single ? minPrice : html`${minPrice} → ${maxPrice}`}</span>
      </div>
    `;
  }

  private renderTags() {
    const card = this.card;
    const isK8s =
      card.workloadType === 'kubernetes' &&
      card.nodeMin !== undefined &&
      card.nodeMax !== undefined;

    return html`
      <div class="provider-card-tags">
        ${isK8s
          ? html`<span class="tag tag--accent">
              <img
                class="k8s-icon"
                src=${assetUrl(this.apiBase, '/assets/icons/base/kuber.svg')}
                alt=""
                aria-hidden="true"
              />
              k8s
            </span>`
          : nothing}
        ${card.gpu ? html`<span class="tag">${card.gpu}</span>` : nothing}
        ${card.vram ? html`<span class="tag">${card.vram} VRAM</span>` : nothing}
        ${isK8s
          ? html`<span class="tag tag--accent">${k8sNodeCountLabel(card)}</span>`
          : nothing}
        ${card.nodeSpec ? html`<span class="tag">${card.nodeSpec}</span>` : nothing}
        ${card.nodeSpec
          ? nothing
          : html`
              <span class="tag">${card.vcpu} vCPU</span>
              <span class="tag">${card.ram} RAM</span>
              ${card.volumeGb
                ? html`<span class="tag"
                    >${card.volumeGb} GB${card.volumeType
                      ? html` ${card.volumeType.toLowerCase()}`
                      : nothing}
                    disk</span
                  >`
                : nothing}
            `}
        ${card.os ? html`<span class="tag">${card.os}</span>` : nothing}
        ${card.isSpot
          ? html`<span class="tag tag--accent">
              <img
                class="spot-icon"
                src=${assetUrl(this.apiBase, '/assets/icons/base/spot.svg')}
                alt=""
                aria-hidden="true"
              />
              spot
            </span>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-card': EmmaChatCard;
  }
}
